import { BaseApiService } from './BaseApiService';
import { formatDate, formatDateTime } from '../utils/date';

export interface SchedulerConfig {
  id: number;
  max_lookahead_days: number;
  bulk_grouping_enabled: boolean;
  product_grouping_enabled: boolean;
  shelf_life_buffer_days: number;
  daily_auto_run_enabled: boolean;
  daily_run_time: string;
  capacity_buffer_percent: number;
  risk_threshold_percent: number;
  created_at: string;
  updated_at: string;
}

export interface SchedulerRun {
  id: number;
  run_type: 'manual' | 'daily_auto';
  orders_processed: number;
  orders_scheduled_on_time: number;
  orders_delayed: number;
  capacity_overflow_detected: boolean;
  notifications_created: number;
  bulk_groupings_created: number;
  product_groupings_created: number;
  total_hours_scheduled: number;
  total_hours_available: number;
  optimization_score: number;
  run_duration_ms: number;
  run_by?: number;
  created_at: string;
}

export interface OptimizationResult {
  success: boolean;
  ordersProcessed: number;
  ordersScheduledOnTime: number;
  ordersDelayed: number;
  bulkGroupingsCreated: number;
  productGroupingsCreated: number;
  optimizationScore: number;
  capacityOverflowDetected: boolean;
  notificationsCreated: number;
  runDurationMs: number;
  schedulerRunId: number;
}

export interface OptimizationPreview {
  ordersToProcess: number;
  capacityAnalysis: {
    totalRequiredHours: number;
    totalAvailableHours: number;
    isOverCapacity: boolean;
    overflowHours: number;
    affectedOrdersCount: number;
    earliestPossibleCompletion: string;
  } | null;
  estimatedOptimizations: {
    bulkGroupings: number;
    productGroupings: number;
    estimatedTimeSaved: number;
  };
}

export interface Notification {
  id: number;
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  message: string;
  data?: any;
  is_read: boolean;
  created_by?: number;
  created_at: string;
  expires_at?: string;
}

class SchedulerService extends BaseApiService {
  // Run manual optimization
  async runOptimization(): Promise<OptimizationResult> {
    const response = await this.post<{ success: boolean; data: OptimizationResult }>(
      '/scheduler/optimize',
      {}
    );
    if (!response.data) {
      throw new Error('Failed to run optimization');
    }
    return response.data;
  }

  // Get optimization preview
  async getOptimizationPreview(): Promise<OptimizationPreview> {
    const response = await this.get<{ success: boolean; data: OptimizationPreview }>(
      '/scheduler/preview'
    );
    if (!response.data) {
      throw new Error('Failed to get optimization preview');
    }
    return response.data;
  }

  // Get scheduler configuration
  async getConfig(): Promise<SchedulerConfig> {
    const response = await this.get<{ success: boolean; data: SchedulerConfig }>(
      '/scheduler/config'
    );
    if (!response.data) {
      throw new Error('Failed to get scheduler config');
    }
    return response.data;
  }

  // Update scheduler configuration
  async updateConfig(config: Partial<SchedulerConfig>): Promise<SchedulerConfig> {
    const response = await this.put<{ success: boolean; data: SchedulerConfig }>(
      '/scheduler/config',
      config
    );
    if (!response.data) {
      throw new Error('Failed to update scheduler config');
    }
    return response.data;
  }

  // Get scheduler run history
  async getHistory(limit: number = 50): Promise<SchedulerRun[]> {
    const params = { limit: limit.toString() };
    const response = await this.get<{ success: boolean; data: SchedulerRun[] }>(
      '/scheduler/history',
      params
    );
    return response.data || [];
  }

  // Get scheduler notifications
  async getNotifications(filters?: {
    type?: string;
    severity?: string;
    is_read?: boolean;
    limit?: number;
  }): Promise<Notification[]> {
    const params: Record<string, string> = {};
    if (filters?.type) params.type = filters.type;
    if (filters?.severity) params.severity = filters.severity;
    if (filters?.is_read !== undefined) params.is_read = filters.is_read.toString();
    if (filters?.limit) params.limit = filters.limit.toString();

    const response = await this.get<{ success: boolean; data: Notification[] }>(
      '/scheduler/notifications',
      params
    );
    return response.data || [];
  }

  // Get dashboard notifications
  async getDashboardNotifications(limit: number = 5): Promise<{
    notifications: Notification[];
    unreadCount: number;
  }> {
    const params = { limit: limit.toString() };
    const response = await this.get<{
      success: boolean;
      data: { notifications: Notification[]; unreadCount: number };
    }>('/scheduler/notifications/dashboard', params);

    return response.data || { notifications: [], unreadCount: 0 };
  }

  // Mark notification as read
  async markNotificationAsRead(id: number): Promise<Notification> {
    const response = await this.put<{ success: boolean; data: Notification }>(
      `/scheduler/notifications/${id}/read`,
      {}
    );
    if (!response.data) {
      throw new Error('Failed to mark notification as read');
    }
    return response.data;
  }

  // Mark all notifications as read
  async markAllNotificationsAsRead(): Promise<{ count: number }> {
    const response = await this.put<{ success: boolean; data: { count: number } }>(
      '/scheduler/notifications/read-all',
      {}
    );
    return response.data || { count: 0 };
  }

  // Utility functions
  formatDuration(milliseconds: number): string {
    if (milliseconds < 1000) {
      return `${milliseconds}ms`;
    } else if (milliseconds < 60000) {
      return `${(milliseconds / 1000).toFixed(1)}s`;
    } else {
      return `${(milliseconds / 60000).toFixed(1)}m`;
    }
  }

  // Use centralized date utilities instead of duplicate methods
  formatDateTime(dateString: string): string {
    return formatDateTime(dateString);
  }

  formatDate(dateString: string): string {
    return formatDate(dateString);
  }

  getSeverityColor(severity: string): string {
    switch (severity) {
      case 'critical':
        return '#f44336'; // red
      case 'high':
        return '#ff9800'; // orange
      case 'medium':
        return '#2196f3'; // blue
      case 'low':
        return '#4caf50'; // green
      default:
        return '#757575'; // gray
    }
  }

  getSeverityIcon(severity: string): string {
    switch (severity) {
      case 'critical':
        return '🚨';
      case 'high':
        return '⚠️';
      case 'medium':
        return 'ℹ️';
      case 'low':
        return '✅';
      default:
        return '📋';
    }
  }

  getOptimizationScoreColor(score: number): string {
    if (score >= 90) return '#4caf50'; // green
    if (score >= 70) return '#8bc34a'; // light green
    if (score >= 50) return '#ff9800'; // orange
    return '#f44336'; // red
  }

  getOptimizationScoreLabel(score: number): string {
    if (score >= 90) return 'Excellent';
    if (score >= 70) return 'Good';
    if (score >= 50) return 'Fair';
    return 'Poor';
  }
}

export const schedulerService = new SchedulerService();
export default schedulerService;
